import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { StyledDialogTitle } from '@/components/MobileApproval/ApprovalContent/styles';
import { DialogCloseButton } from '@/styles/styles';
import { NinetyViewPortHeightIframeWithLoading } from '@/components/IframeWithLoading';
import ApprovalView from '@/components/Approval/ApprovalView';
import ApprovalCreate from '@/components/Approval/ApprovalCreate';
import UnsavedCommentConfirmModal from '@/components/Approval/ApprovalView/UnsavedCommentConfirmModal';
import NextApprovalConfirmModal from '@/components/Approval/NextApprovalConfirmModal';
import useApprovalEditMode, {
  EditModeLoading,
} from '@/components/Approval/@hooks/useApprovalEditMode';
import useNextApprovalDoc from '@/components/Approval/@hooks/useNextApprovalDoc';
import useLegacyNextDocMessage from '@/components/Approval/@hooks/useLegacyNextDocMessage';
import type { DocumentStatus } from '@/components/Approval/ApprovalView/types';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import { useDialog } from '@/hooks/global-tools';
import { NATIVE_APPROVAL_TYPES, NATIVE_VIEW_APPROVAL_TYPES } from '@/constant';
import { HighZIndex } from '@/constant/z-index';
import { getIntranetDocUrl } from '@/utils/urls';
import { isRnWebView, RnMessage, sendToRN } from '@/utils/function';

type P = {
  open: boolean;
  docId: string;
  /** 목록 응답의 type.code. 미확정이면 레거시로 폴백 */
  apvType?: number;
  onClose: () => void;
  /** 삭제·회수·결재·편집저장 후 호스트 목록 재검증 */
  onRefresh?: () => void;
};

/** 모바일 조회 모달. 레거시 유형은 별도 레포라 iframe */
const ApprovalViewDialog = ({
  open,
  docId,
  apvType,
  onClose,
  onRefresh,
}: P) => {
  const dialog = useDialog();
  const commentDirtyRef = useRef<(() => boolean) | null>(null);
  const formDirtyRef = useRef<(() => boolean) | null>(null);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  // 결재 후 다음 문서로 교체되므로 표시 대상은 props 가 아니라 내부 상태
  const [viewDoc, setViewDoc] = useState({ docId, apvType });

  const isNativeType =
    typeof viewDoc.apvType === 'number' &&
    NATIVE_VIEW_APPROVAL_TYPES.includes(viewDoc.apvType);

  /*
   * 프리페치는 열려 있는 동안만 — 목록 화면에 상시 마운트되는 컴포넌트라 닫힌 채로 요청하면 낭비.
   * 레거시 양식은 다음 문서 선정이 레거시 몫이라 조회 자체가 불필요 + 큐 불일치 유발.
   */
  const { nextDoc, promptNext, clearNextDoc } = useNextApprovalDoc(
    viewDoc.docId,
    open && isNativeType,
  );

  // 레거시 iframe 을 띄우고 있는 동안만 수신 — 표시 문서만 스왑(레거시가 이미 확인을 받은 뒤)
  useLegacyNextDocMessage(open && !isNativeType, (next) => {
    setViewDoc({ docId: next.docId, apvType: next.typeCode });
    clearNextDoc();
  });

  // 편집 폼 preload 는 작성 화이트리스트 기준 — 조회전용 유형은 V2 작성폼이 없음
  const isEditSupported =
    typeof viewDoc.apvType === 'number' &&
    NATIVE_APPROVAL_TYPES.includes(viewDoc.apvType);

  const { isEditMode, setIsEditMode, initialFormState } = useApprovalEditMode(
    isEditSupported ? viewDoc.docId : null,
  );

  // 재오픈 시 표시 문서·편집 모드·레이어 초기화. open 도 봐야 함 —
  // 다음 문서로 스왑한 뒤 같은 docId 로 다시 열면 props 가 그대로라 스왑된 문서가 남음
  useEffect(() => {
    if (!open) return; // 닫히는 중에는 유지 — 종료 애니메이션 동안 내용이 바뀌어 보임
    setViewDoc({ docId, apvType });
    setIsEditMode(false);
    setShowUnsavedConfirm(false);
    clearNextDoc();
  }, [open, docId, apvType]);

  const doClose = () => {
    setShowUnsavedConfirm(false);
    setIsEditMode(false);
    clearNextDoc();
    onClose();
  };

  /** 결재(승인/반려) 성공 후 — 데스크탑 모달과 동일하게 다음 결재 문서 레이어, 없으면 닫기. */
  const handleDecided = async (action: ApprovalDecisionAction) => {
    onRefresh?.();
    // 보류(H)는 결재 큐에 남아 본인이 재처리 — 화면 유지
    if (action === 'hold') return;
    const { next, stale } = await promptNext(viewDoc.docId, action);
    if (!next && !stale) doClose();
  };

  /** 삭제 완료 시 앱은 웹뷰를 닫아 Approval 목록으로 복귀 — 포탈 목록으로 보내면 웹뷰에 갇힘 */
  const handleStatusChange = (status: DocumentStatus) => {
    onRefresh?.();
    if (status === 'D' && isRnWebView()) sendToRN(RnMessage.BACK);
  };

  // 닫기 전 가드: (1) 편집 중 폼에 변경분 있으면 확인 팝업, (2) 작성 중 코멘트 있으면 가드 모달
  const handleCloseWithReset = () => {
    if (isEditMode && formDirtyRef.current?.()) {
      dialog.show({
        message:
          '작성화면을 닫으시겠어요?\n임시저장 되지 않은 내용은 사라집니다.',
        confirmButtonText: '나가기',
        cancelButtonText: '취소',
        onConfirm: doClose,
        forceToFront: true,
      });
      return;
    }
    if (commentDirtyRef.current?.()) {
      setShowUnsavedConfirm(true);
      return;
    }
    doClose();
  };

  return (
    <>
      {/*
        z-index 는 MUI 기본(1300) 유지 — 올리면 안에서 여는 모달·확인팝업(의견입력/삭제확인 등)이
        전부 뒤에 깔려 안 보임. 기본값이면 나중에 마운트되는 중첩 모달이 포탈 순서대로 위에 쌓임.
      */}
      <Dialog
        fullWidth
        maxWidth={'lg'}
        fullScreen={true}
        open={open}
        onClose={handleCloseWithReset}
      >
        <StyledDialogTitle>
          {'Approval'}
          <DialogCloseButton
            id={'dialog-close-button'}
            onClick={handleCloseWithReset}
          />
        </StyledDialogTitle>
        <DialogContent style={{ padding: '0' }} dividers>
          {isNativeType ? (
            <>
              {/* 편집은 display 숨김만 — unmount 시 AI 요약 retry 가드 초기화로 over-call 됨 */}
              <div style={{ display: isEditMode ? 'none' : 'contents' }}>
                <ApprovalView
                  documentId={viewDoc.docId}
                  apvType={viewDoc.apvType}
                  onClose={handleCloseWithReset}
                  onStatusChange={handleStatusChange}
                  onDecided={handleDecided}
                  onEdit={() => setIsEditMode(true)}
                  commentDirtyRef={commentDirtyRef}
                />
              </div>
              {isEditMode &&
                (initialFormState ? (
                  <ApprovalCreate
                    apvType={viewDoc.apvType as number}
                    mode="edit"
                    documentId={viewDoc.docId}
                    initialFormState={initialFormState}
                    onSubmitSuccess={() => {
                      onRefresh?.();
                      setIsEditMode(false);
                    }}
                    onExit={() => setIsEditMode(false)}
                    exitGuardRef={formDirtyRef}
                  />
                ) : (
                  <EditModeLoading />
                ))}
            </>
          ) : (
            <NinetyViewPortHeightIframeWithLoading
              url={getIntranetDocUrl(viewDoc.docId, false)}
            />
          )}
        </DialogContent>
      </Dialog>
      <UnsavedCommentConfirmModal
        open={showUnsavedConfirm}
        zIndex={HighZIndex}
        onConfirmClose={doClose}
        onKeepEditing={() => setShowUnsavedConfirm(false)}
      />
      <NextApprovalConfirmModal
        open={!!nextDoc}
        onOpenNext={() => {
          if (!nextDoc) return;
          // 다이얼로그는 유지 — 표시 문서만 스왑
          setViewDoc({ docId: nextDoc.docId, apvType: nextDoc.typeCode });
          clearNextDoc();
        }}
        onClose={doClose}
      />
    </>
  );
};

export default ApprovalViewDialog;
